const ABBREVIATION_THRESHOLD = 10_000;

// Pinned to en-US so formatted output is deterministic regardless of the
// host / runtime locale (a non-en-US, e.g. RTL, locale alters the grouping
// separator and prepends a U+200E bidi mark to negatives).
const NUMBER_LOCALE = 'en-US';

const compactFormatter = new Intl.NumberFormat(NUMBER_LOCALE, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat(NUMBER_LOCALE);

/**
 * Formats a count value for compact UI display.
 * - Below 10,000: formatted with thousand separators (e.g. 1,200)
 * - 10,000+: compact notation with one decimal (e.g. 10,402 → "10.4k", 1,200,000 → "1.2m")
 * Abbreviated values include the full comma-formatted number as a tooltip.
 *
 * @param value - The numeric count to format
 * @returns An object with `display` (the formatted string) and optional `tooltip`
 *          (the full comma-formatted value, only set when abbreviated)
 */
export const formatCount = (
  value: number,
): { display: string; tooltip?: string } => {
  if (value < ABBREVIATION_THRESHOLD) {
    return { display: formatNumber(value) };
  }

  return {
    display: compactFormatter.format(value).toLowerCase(),
    tooltip: formatNumber(value),
  };
};

/**
 * Formats a number with thousand separators, pinned to en-US (see
 * NUMBER_LOCALE) so the output is deterministic regardless of the host /
 * runtime locale and safe for consumers that string-compare the result.
 * Reuses a module-scope formatter (formatCount delegates here from table
 * cell renderers, so per-call instantiation would allocate on a hot path).
 * Example: 1000 -> "1,000", 1234567 -> "1,234,567", -1234 -> "-1,234"
 *
 * @param value - The number to format
 * @returns The formatted number string with thousand separators
 */
export const formatNumber = (
  value: number | string | undefined | null,
): string => {
  if (!value) {
    return '0';
  }

  return numberFormatter.format(Math.floor(Number(value)));
};

/**
 * Formats a USD amount for compact UI display, without fraction digits.
 * Example: 30 -> "$30", 600 -> "$600".
 *
 * @param value - The dollar amount to format
 * @returns The formatted currency string
 */
export const formatCurrency = (value: number): string =>
  currencyFormatter.format(value);
